import './styles.scss';

import { useEffect, useMemo } from 'react';
import useSession, { SessionUser } from '@/state/session';

import IncidentHistoryForm from '@/features/IncidentHistory/components/IncidentHistoryForm';
import { IncidentHistoryFormDefinitions } from '@/features/IncidentHistory/components/IncidentHistoryForm/types';
import { IncidentIssueWithCache } from '@/models/gen/graphql';
import Popup from '@/common/Popup';
import { PopupTypes } from '@/common/Popup/types';
import { createComponentState } from '@/state';
import { getClasses } from '@/utils';
import useConfirmation from '@/hooks/useConfirmation';
import { useNavigate } from 'react-router-dom';

export type IncidentHistoryDrawerState = {
  show: boolean;
  data: Partial<IncidentIssueWithCache>;
};
export const initIncidentHistoryState: IncidentHistoryDrawerState = {
  show: false,
  data: {},
};
export const useIncidentHistoryDrawer = createComponentState<IncidentHistoryDrawerState>(initIncidentHistoryState);
export type IncidentHistoryDrawerProps = { locale?: Record<string, string> } & PopupTypes.Props;

const IncidentHistoryDrawer = ({ locale: customLocale, ...drawerProps }: IncidentHistoryDrawerProps): JSX.Element => {
  const [state, setState] = useIncidentHistoryDrawer(({ state, setState }) => [state, setState]);
  const { show, data } = state;
  const user = useSession(({ state }) => state.user || ({} as SessionUser));
  const navigate = useNavigate();
  const confirmHide = useConfirmation({
    Body: {
      message: (
        <div className="IssueConfirmation">
          <h5>Are you sure?</h5>
          <span>Any unsaved changes will be lost.</span>
        </div>
      ),
    },
  });

  const onHide = async (redirect: boolean = false): Promise<void> => {
    try {
      if (redirect) {
        IncidentHistoryFormDefinitions.clearForm();
        setState({ ...state, show: false });
      } else {
        const issueValue = IncidentHistoryFormDefinitions.loadForm();
        const issuesHasValue = Object.keys(issueValue).length > 0;
        if (issuesHasValue) await confirmHide();
        setState({ ...state, show: false });
      }
    } catch (err) {
      console.error('Error hiding drawer:', err);
    }
  };
  const onExited = (): void => setState(initIncidentHistoryState);

  const onSubmit = (data): void => {
    debug.log('incident-history,incident-history.drawer', 'Submit Incident', data);
    IncidentHistoryFormDefinitions.clearForm();
    onHide();
  };

  const gotoHistory = (): void => {
    onHide(true);
    navigate('/incident-history');
  };

  const actions = useMemo(() => {
    const result = [];
    if (!location.pathname.startsWith('/incident-history'))
      result.push({
        key: 'history',
        label: 'See History',
        onClick: gotoHistory,
      });
    return result.length > 0 ? result : undefined;
  }, [location.pathname, gotoHistory]);

  const value = useMemo(() => {
    if (!show) return undefined;
    const isDispatcher = user?.role?.name === 'dispatch';
    const dispatcherId = isDispatcher && !data?.dispatcherId ? user?.id : data?.dispatcherId;
    const result = { ...data, dispatcherId };
    debug.log('incident-history,incident-history.drawer', 'Default Value', result);
    return result;
  }, [show, data, user]);

  useEffect((): void => {
    if (show) return;
    IncidentHistoryFormDefinitions.clearForm();
  }, [show]);

  return (
    <Popup
      {...drawerProps}
      className={getClasses('IncidentHistoryDrawer', drawerProps.className)}
      show={show}
      onHide={onHide}
      onExited={onExited}
      actions={actions}
    >
      <IncidentHistoryForm onSubmit={onSubmit} value={value} key={data?.id} />
    </Popup>
  );
};

export default IncidentHistoryDrawer;
